import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MousePointerClick, Percent, Trophy, Users } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import StatCard from "@/components/admin/StatCard";

export default function Overview() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/timeseries?days=30"),
      api.get("/analytics/top-products?limit=5"),
    ])
      .then(([o, s, t]) => {
        setOverview(o.data);
        setSeries(s.data);
        setTop(t.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="overview-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black/10 border-t-black" />
      </div>
    );
  }

  const maxClicks = Math.max(1, ...top.map((t) => t.clicks));
  const hasData = (overview?.total_clicks || 0) > 0 || (overview?.total_visits || 0) > 0;

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-zinc-500">How your TECH iELA page is performing.</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <StatCard icon={Users} label="Visitors" value={overview?.total_visits ?? 0} sub={`+${overview?.visits_7d ?? 0} this week`} testid="stat-visitors" />
        <StatCard icon={MousePointerClick} label="Product Clicks" value={overview?.total_clicks ?? 0} sub={`+${overview?.clicks_7d ?? 0} this week`} testid="stat-clicks" />
        <StatCard icon={Percent} label="CTR" value={`${overview?.ctr ?? 0}%`} sub="clicks per visit" testid="stat-ctr" />
        <StatCard
          icon={Trophy}
          label="Top Product"
          value={overview?.top_product ? overview.top_product.name : "—"}
          sub={overview?.top_product ? `${overview.top_product.clicks} clicks` : "no clicks yet"}
          testid="stat-top-product"
        />
      </motion.div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-black/5 bg-white p-6 lg:col-span-2" data-testid="clicks-chart-card">
          <h2 className="font-heading text-lg font-medium tracking-tight">Clicks over time</h2>
          <p className="text-xs text-zinc-400">Last 30 days</p>
          {hasData ? (
            <div className="mt-6 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#111111" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#111111" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#a1a1aa" }} tickLine={false} axisLine={false} interval={4} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#a1a1aa" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
                    cursor={{ stroke: "rgba(0,0,0,0.15)" }}
                  />
                  <Area type="monotone" dataKey="visits" stroke="#a1a1aa" strokeWidth={1.5} fill="transparent" name="Visits" />
                  <Area type="monotone" dataKey="clicks" stroke="#111111" strokeWidth={2} fill="url(#clicksFill)" name="Clicks" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="mt-6 flex h-64 flex-col items-center justify-center rounded-2xl bg-zinc-50 text-center" data-testid="chart-empty-state">
              <MousePointerClick className="h-6 w-6 text-zinc-300" />
              <p className="mt-3 text-sm text-zinc-500">No activity yet. Share your page to start seeing data.</p>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-black/5 bg-white p-6" data-testid="top-products-card">
          <h2 className="font-heading text-lg font-medium tracking-tight">Top products</h2>
          <p className="text-xs text-zinc-400">All-time clicks</p>
          {top.length === 0 ? (
            <div className="mt-6 flex flex-col items-center rounded-2xl bg-zinc-50 py-10 text-center" data-testid="top-products-empty">
              <Trophy className="h-6 w-6 text-zinc-300" />
              <p className="mt-3 text-sm text-zinc-500">Clicks will appear here.</p>
            </div>
          ) : (
            <ul className="mt-6 space-y-5">
              {top.map((p, i) => (
                <li key={p.product_id} data-testid={`top-product-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm font-medium">
                      <span className="mr-2 font-mono text-xs text-zinc-300">{String(i + 1).padStart(2, "0")}</span>
                      {p.name}
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-zinc-500">{p.clicks}</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-100">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(p.clicks / maxClicks) * 100}%` }}
                      transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-full bg-black"
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
